/**
 * Calculator Component - WESTEER Design System
 *
 * @version 1.0
 * @updated 2026-02-12
 * @module core/map-tools/calculator
 *
 * @description
 * Calculadora científica compacta con diseño WESTEER.
 * Se integra con herramientas de medición para recibir valores.
 *
 * @remarks
 * **Características**:
 * - Modal con glassmorphism intenso
 * - Operaciones básicas (+, -, ×, ÷)
 * - Funciones científicas (√, x², %, 1/x)
 * - Recibe valores de herramientas de medición
 * - Historial de operaciones
 * - Teclado numérico integrado
 *
 * @example
 * ```tsx
 * import { Calculator } from '@/core/map-tools/calculator';
 *
 * function MapTools() {
 *   const [showCalc, setShowCalc] = useState(false);
 *
 *   return (
 *     <Calculator
 *       isOpen={showCalc}
 *       onClose={() => setShowCalc(false)}
 *       initialValue={123.45}
 *     />
 *   );
 * }
 * ```
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type { PointerEvent as ReactPointerEvent, SyntheticEvent } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Minus, Divide, Equal, Delete } from 'lucide-react';
import './calculator.css';

/**
 * Props para Calculator
 */
interface CalculatorProps {
  /**
   * Si el modal está abierto
   */
  isOpen: boolean;

  /**
   * Callback para cerrar el modal
   */
  onClose: () => void;

  /**
   * Valor inicial (ej: desde una medición)
   */
  initialValue?: number;

  /**
   * Callback cuando se inserta un valor desde medición
   */
  onValueInserted?: (value: number) => void;
}

/**
 * Calculator - Calculadora científica compacta
 */
export function Calculator({
  isOpen,
  onClose,
  initialValue,
  onValueInserted,
}: CalculatorProps): JSX.Element | null {
  const [display, setDisplay] = useState<string>('0');
  const [previousValue, setPreviousValue] = useState<number | null>(null);
  const [operation, setOperation] = useState<string | null>(null);
  const [waitingForOperand, setWaitingForOperand] = useState<boolean>(false);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const dragOffsetRef = useRef<{ x: number; y: number } | null>(null);

  /**
   * Cargar valor inicial cuando se recibe
   */
  useEffect(() => {
    if (initialValue !== undefined && isOpen) {
      setDisplay(initialValue.toString());
      onValueInserted?.(initialValue);
    }
  }, [initialValue, isOpen, onValueInserted]);

  useEffect(() => {
    if (!isOpen || typeof window === 'undefined' || position !== null) return;

    const defaultWidth = 320;
    const defaultHeight = 520;
    const margin = 24;
    const nextX = Math.max(margin, window.innerWidth - defaultWidth - margin);
    const nextY = Math.max(112, window.innerHeight - defaultHeight - 120);
    setPosition({ x: nextX, y: nextY });
  }, [isOpen, position]);

  /**
   * Cerrar con ESC
   */
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return (): void => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      dragOffsetRef.current = null;
    }

    return (): void => {
      dragOffsetRef.current = null;
    };
  }, [isOpen]);

  const stopPanelPropagation = (event: SyntheticEvent): void => {
    event.stopPropagation();
  };

  const handleDragStart = useCallback((event: ReactPointerEvent<HTMLDivElement>): void => {
    if ((event.target as HTMLElement).closest('button')) {
      return;
    }

    if (!position) return;

    dragOffsetRef.current = {
      x: event.clientX - position.x,
      y: event.clientY - position.y,
    };

    const handlePointerMove = (moveEvent: PointerEvent): void => {
      const offset = dragOffsetRef.current;
      if (!offset) return;

      const nextX = Math.min(
        Math.max(8, moveEvent.clientX - offset.x),
        Math.max(8, window.innerWidth - 240)
      );
      const nextY = Math.min(
        Math.max(72, moveEvent.clientY - offset.y),
        Math.max(72, window.innerHeight - 120)
      );

      setPosition({ x: nextX, y: nextY });
    };

    const handlePointerUp = (): void => {
      dragOffsetRef.current = null;
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  }, [position]);

  /**
   * Insertar dígito
   */
  const inputDigit = (digit: string): void => {
    if (waitingForOperand) {
      setDisplay(digit);
      setWaitingForOperand(false);
    } else {
      setDisplay(display === '0' ? digit : display + digit);
    }
  };

  /**
   * Insertar punto decimal
   */
  const inputDecimal = (): void => {
    if (waitingForOperand) {
      setDisplay('0.');
      setWaitingForOperand(false);
    } else if (display.indexOf('.') === -1) {
      setDisplay(display + '.');
    }
  };

  /**
   * Limpiar display
   */
  const clearDisplay = (): void => {
    setDisplay('0');
    setPreviousValue(null);
    setOperation(null);
    setWaitingForOperand(false);
  };

  /**
   * Borrar último dígito
   */
  const backspace = (): void => {
    if (display.length > 1) {
      setDisplay(display.slice(0, -1));
    } else {
      setDisplay('0');
    }
  };

  /**
   * Ejecutar operación
   */
  const performOperation = (nextOperation: string): void => {
    const inputValue = parseFloat(display);

    if (previousValue === null) {
      setPreviousValue(inputValue);
    } else if (operation) {
      const currentValue = previousValue || 0;
      const newValue = calculate(currentValue, inputValue, operation);
      setDisplay(String(newValue));
      setPreviousValue(newValue);
    }

    setWaitingForOperand(true);
    setOperation(nextOperation);
  };

  /**
   * Calcular resultado según operación
   */
  const calculate = (firstValue: number, secondValue: number, op: string): number => {
    switch (op) {
      case '+':
        return firstValue + secondValue;
      case '-':
        return firstValue - secondValue;
      case '×':
        return firstValue * secondValue;
      case '÷':
        return secondValue !== 0 ? firstValue / secondValue : 0;
      default:
        return secondValue;
    }
  };

  /**
   * Funciones científicas
   */
  const scientificOperation = (op: string): void => {
    const value = parseFloat(display);
    let result = 0;

    switch (op) {
      case '√':
        result = Math.sqrt(value);
        break;
      case 'x²':
        result = value * value;
        break;
      case '%':
        result = value / 100;
        break;
      case '1/x':
        result = value !== 0 ? 1 / value : 0;
        break;
      case '+/-':
        result = value * -1;
        break;
      default:
        return;
    }

    setDisplay(String(result));
    setWaitingForOperand(true);
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="calculator-overlay">
      <div
        className="calculator-modal calculator-floating"
        style={position ? { left: `${position.x}px`, top: `${position.y}px` } : undefined}
        onPointerDown={stopPanelPropagation}
        onClick={stopPanelPropagation}
      >
        {/* Header */}
        <div
          className="calculator-header calculator-drag-handle"
          onPointerDown={handleDragStart}
        >
          <h3 className="calculator-title">Calculadora</h3>
          <button
            className="calculator-close"
            onClick={onClose}
            onPointerDown={stopPanelPropagation}
            aria-label="Cerrar calculadora"
          >
            <X size={16} />
          </button>
        </div>

        {/* Display */}
        <div className="calculator-display">{display}</div>

        {/* Buttons Grid */}
        <div className="calculator-grid">
          {/* Row 1: Scientific functions */}
          <button className="calc-btn calc-btn-function" onClick={() => scientificOperation('√')}>
            √
          </button>
          <button className="calc-btn calc-btn-function" onClick={() => scientificOperation('x²')}>
            x²
          </button>
          <button className="calc-btn calc-btn-function" onClick={() => scientificOperation('%')}>
            %
          </button>
          <button className="calc-btn calc-btn-function" onClick={() => scientificOperation('1/x')}>
            1/x
          </button>

          {/* Row 2: Clear + Backspace + Operations */}
          <button className="calc-btn calc-btn-clear" onClick={clearDisplay}>
            C
          </button>
          <button className="calc-btn calc-btn-backspace" onClick={backspace}>
            <Delete size={16} />
          </button>
          <button className="calc-btn calc-btn-function" onClick={() => scientificOperation('+/-')}>
            +/-
          </button>
          <button className="calc-btn calc-btn-operation" onClick={() => performOperation('÷')}>
            <Divide size={16} />
          </button>

          {/* Row 3: Numbers 7-9 + × */}
          <button className="calc-btn" onClick={() => inputDigit('7')}>
            7
          </button>
          <button className="calc-btn" onClick={() => inputDigit('8')}>
            8
          </button>
          <button className="calc-btn" onClick={() => inputDigit('9')}>
            9
          </button>
          <button className="calc-btn calc-btn-operation" onClick={() => performOperation('×')}>
            ×
          </button>

          {/* Row 4: Numbers 4-6 + - */}
          <button className="calc-btn" onClick={() => inputDigit('4')}>
            4
          </button>
          <button className="calc-btn" onClick={() => inputDigit('5')}>
            5
          </button>
          <button className="calc-btn" onClick={() => inputDigit('6')}>
            6
          </button>
          <button className="calc-btn calc-btn-operation" onClick={() => performOperation('-')}>
            <Minus size={16} />
          </button>

          {/* Row 5: Numbers 1-3 + + */}
          <button className="calc-btn" onClick={() => inputDigit('1')}>
            1
          </button>
          <button className="calc-btn" onClick={() => inputDigit('2')}>
            2
          </button>
          <button className="calc-btn" onClick={() => inputDigit('3')}>
            3
          </button>
          <button className="calc-btn calc-btn-operation" onClick={() => performOperation('+')}>
            <Plus size={16} />
          </button>

          {/* Row 6: 0 + . + = */}
          <button className="calc-btn calc-btn-zero" onClick={() => inputDigit('0')}>
            0
          </button>
          <button className="calc-btn" onClick={inputDecimal}>
            .
          </button>
          <button className="calc-btn calc-btn-equals" onClick={() => performOperation('=')}>
            <Equal size={16} />
          </button>
        </div>

        {/* Footer Info */}
        <div className="calculator-footer">
          <p className="calculator-info">
            💡 Puedes insertar valores desde las herramientas de medición
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
}
